// src/layouts/AppLayout.jsx
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

import Header from "../components/Header";
import MobileHeader from "../components/MobileHeader";
import MobileTabBar from "../components/MobileTabBar";
import Footer from "../components/Footer";
import PageTransition from "../components/PageTransition";
import SplashScreen from "../components/SplashScreen";
import FloatingActionButtons from "../components/FloatingActionButtons";

import { AuthService } from "../services/auth";
import { getMyActiveSubscription, getMyTenantPass, getRolesStatuses } from "../services/subscription";
import { getVisibleTabKeys, getVisibleHeaderKeys } from "../utils/rolePermissions";

// Cache simple en mémoire pour éviter les rechargements inutiles
const CACHE_DURATION = 30000; // 30 secondes
const userCache = {
  data: null,
  timestamp: 0,
  rolesData: null,
  rolesTimestamp: 0,
};

// Exposer le cache globalement pour les autres composants
if (typeof window !== 'undefined') {
  window.__userCache = userCache;
}

export default function AppLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Vérifier si le splash a déjà été affiché dans cette session
  const hasSeenSplash = sessionStorage.getItem('splash_shown') === 'true';
  const isFirstLoad = pathname === '/' && !hasSeenSplash;
  
  const [showSplash, setShowSplash] = useState(isFirstLoad);

  // -------------------- ÉTATS AUTH / PREMIUM --------------------
  const [me, setMe] = useState(null);
  const [subs, setSubs] = useState(null);            // { active, started_at, ends_at, plan:{...} }
  const [tenantPass, setTenantPass] = useState(null); // { activated, activated_at, reference }
  const [rolesStatuses, setRolesStatuses] = useState(null); // Nouvelle API multi-rôles
  const [loaded, setLoaded] = useState(false);

  // -------------------- VISIBILITÉ TAB BAR ----------------------
  const HIDE_EXACT = new Set([
    "/connexion",
    "/paiement",
    "/activation-paiement",
    "/inscription-vendeur",
    "/inscription-locataire",
    "/messagerie",
  ]);
  const HIDE_PREFIX = ["/ajouter", "/ajouter-location", "/propriete/", "/location/", "/listing/", "/agent-com/"];

  const hideTabBar = HIDE_EXACT.has(pathname) || HIDE_PREFIX.some((p) => pathname.startsWith(p));
  const showBack = hideTabBar && pathname !== "/";
  
  // Masquer le header sur les pages de messagerie
  const hideHeader = pathname === "/messagerie" || pathname.startsWith("/agent-com/");

  // -------------------- CHARGEMENT /auth/me ---------------------
  useEffect(() => {
    (async () => {
      try {
        // Vérifier le cache
        const now = Date.now();
        if (userCache.data && (now - userCache.timestamp) < CACHE_DURATION) {
          setMe(userCache.data);
          setLoaded(true);
          return;
        }

        const data = await AuthService.me().catch(() => null);
        if (data) {
          userCache.data = data;
          userCache.timestamp = now;
        }
        setMe(data);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  // --------------- CHARGEMENT PREMIUM (subs / pass) -------------
  useEffect(() => {
    if (!me?.role) {
      setSubs(null);
      setTenantPass(null);
      setRolesStatuses(null);
      return;
    }

    (async () => {
      try {
        // Vérifier le cache pour les rôles
        const now = Date.now();
        let rolesData = null;
        
        if (userCache.rolesData && (now - userCache.rolesTimestamp) < CACHE_DURATION) {
          rolesData = userCache.rolesData;
        } else {
          // Utiliser la nouvelle API multi-rôles
          rolesData = await getRolesStatuses().catch(() => null);
          if (rolesData) {
            userCache.rolesData = rolesData;
            userCache.rolesTimestamp = now;
          }
        }
        
        setRolesStatuses(rolesData);

        if (rolesData?.statuses) {
          // Mettre à jour tenantPass si l'utilisateur a le rôle tenant
          if (rolesData.statuses.tenant) {
            const tenantStatus = rolesData.statuses.tenant;
            setTenantPass({
              activated: tenantStatus.status === "active" && !!tenantStatus.pass,
              activated_at: tenantStatus.pass?.activated_at ?? null,
              reference: tenantStatus.pass?.reference ?? null,
            });
          } else {
            setTenantPass(null);
          }

          // Mettre à jour subs si l'utilisateur a un rôle owner/vendor actif
          let activeSubscription = null;
          let roleAct = rolesData.primary_role;
          let activeOwner = rolesData?.statuses?.owner && rolesData.statuses.owner.status === "active";
          let activeVendor = rolesData?.statuses?.vendor && rolesData.statuses.vendor.status === "active";
          if (roleAct === "owner" && rolesData?.statuses?.owner && rolesData.statuses.owner.status === "active") {
            activeSubscription = rolesData.statuses.owner;
          } else if (rolesData?.statuses?.vendor && rolesData.statuses.vendor.status === "active" && roleAct === "vendor") {
            activeSubscription = rolesData.statuses.vendor;
          }

          if (activeSubscription) {
            setSubs({
              active: activeSubscription.status === "active",
              ends_at: activeSubscription.expires_at ?? null,
              started_at: activeSubscription.activated_at ?? null,
              plan: activeSubscription.plan,
            });
          } else {
            setSubs(null);
          }
        } else {
          // Fallback vers l'ancienne API si la nouvelle n'est pas disponible
          if (me.role === "tenant") {
            const p = await getMyTenantPass().catch(() => null);
            setTenantPass(p);
            setSubs(null);
          } else if (me.role === "owner" || me.role === "vendor") {
            const s = await getMyActiveSubscription().catch(() => null);
            const ends_at = s?.ends_at ?? s?.expires_at ?? null;
            const started_at = s?.started_at ?? s?.activated_at ?? null;
            setSubs(s ? { ...s, ends_at, started_at } : null);
            setTenantPass(null);
          } else {
            setSubs(null);
            setTenantPass(null);
          }
        }
      } catch {
        // ignore
      }
    })();
  }, [me?.role]);

  // -------------------- CLÉS (TABBAR) ---------------------------
  const tabbarKeys = useMemo(() => {
    if (!me?.role) return ["home", "achat", "profile"]; // Pas d'utilisateur connecté
    
    const currentRole = me.role;
    const currentRoleStatus = rolesStatuses?.statuses?.[currentRole];
    
    return getVisibleTabKeys(currentRole, currentRoleStatus, { subs, tenantPass });
  }, [me?.role, subs, tenantPass, rolesStatuses]);

  // -------------------- CLÉS (HEADER) ---------------------------
  const headerKeys = useMemo(() => {
    if (!me?.role) return ["home", "achat", "profile"]; // Pas d'utilisateur connecté
    
    const currentRole = me.role;
    const currentRoleStatus = rolesStatuses?.statuses?.[currentRole];
    
    return getVisibleHeaderKeys(currentRole, currentRoleStatus, { subs, tenantPass });
  }, [me?.role, subs, tenantPass, rolesStatuses]);

  // -------------------- GESTION SPLASH SCREEN --------------------
  const handleSplashComplete = () => {
    setShowSplash(false);
    // Marquer dans sessionStorage que le splash a été affiché
    sessionStorage.setItem('splash_shown', 'true');
  };

  // -------------------- RENDU ---------------------------
  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Splash Screen - uniquement au premier chargement sur la route "/" */}
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}

      {/* Header desktop */}
      {!hideHeader && (
        <div className="hidden md:block sticky top-0 z-40 bg-white/80 backdrop-blur border-b">
          <Header
            visibleKeys={headerKeys}
            user={
              localStorage.getItem("auth_token") && me
                ? {
                    name: me.name || `${me.first_name ?? ""} ${me.last_name ?? ""}`.trim(),
                    avatar: me.avatar,
                  }
                : null
            }
            onSignIn={() => navigate("/connexion")}
          />
        </div>
      )}

      {/* Header mobile */}
      {!hideHeader && (
        <MobileHeader
          user={
            localStorage.getItem("auth_token") && me
              ? {
                  name: me.name || `${me.first_name ?? ""} ${me.last_name ?? ""}`.trim(),
                  avatar: me.avatar,
                }
              : null
          }
          showBack={showBack}
        />
      )}

      {/* Contenu + Transition */}
      <main className={`${hideTabBar ? "" : "pb-[calc(68px+var(--safe-bottom))] md:pb-0"} flex-1`}>
        <PageTransition>
          <Outlet />
        </PageTransition>
      </main>

      {!hideHeader && <Footer />}

      {/* TabBar mobile conditionnelle */}
      {!hideTabBar && (
        <div className="md:hidden fixed bottom-0 inset-x-0 z-40">
          <MobileTabBar visibleKeys={tabbarKeys} />
        </div>
      )}

      {/* Boutons flottants (FAB) */}
      <FloatingActionButtons />
    </div>
  );
}
